import dayjs from 'dayjs'
import { DiagnosticFilters } from '../types'
import { ExamSubtype } from 'types/exam/types'
import { CXRAY_FILTER_SUBTYPES } from '../constants'

/**
 * Verifica se o conjunto de subtipos corresponde ao padrão (cxray_pa/cxray_ap),
 * ignorando a ordem. Usado para manter a URL limpa quando nada foi alterado.
 */
const isDefaultSubtypes = (subtypes: ExamSubtype[]): boolean => {
  if (subtypes.length !== CXRAY_FILTER_SUBTYPES.length) return false
  const set = new Set(subtypes)
  return CXRAY_FILTER_SUBTYPES.every(subtype => set.has(subtype))
}

/**
 * Serializa os filtros para URLSearchParams
 * - Remove campos vazios/null para manter URL legível
 * - Converte datas para formato YYYY-MM-DD
 * - Converte array de tipos diagnósticos para string separada por vírgula
 */
export const serializeFilters = (
  filters: DiagnosticFilters
): URLSearchParams => {
  const params = new URLSearchParams()

  // subtypes - serializar apenas quando difere do padrão (cxray_pa/cxray_ap)
  if (
    filters.subtypes &&
    filters.subtypes.length > 0 &&
    !isDefaultSubtypes(filters.subtypes)
  ) {
    params.append('subtypes', filters.subtypes.join(','))
  }

  // diagnosticTypes - serializar como string separada por vírgula
  if (
    filters.diagnosticTypes &&
    filters.diagnosticTypes.length > 0 &&
    filters.diagnosticTypes[0] !== 'none'
  ) {
    params.append('diagnosticTypes', filters.diagnosticTypes.join(','))
  }

  // examDateStart
  if (filters.examDateStart) {
    params.append('examDateStart', filters.examDateStart.format('YYYY-MM-DD'))
  }

  // examDateEnd
  if (filters.examDateEnd) {
    params.append('examDateEnd', filters.examDateEnd.format('YYYY-MM-DD'))
  }

  // patientId
  if (filters.patientId && filters.patientId.trim() !== '') {
    params.append('patientId', filters.patientId)
  }

  // examId
  if (filters.examId && filters.examId.trim() !== '') {
    params.append('examId', filters.examId)
  }

  // diagnosticId
  if (filters.diagnosticId && filters.diagnosticId.trim() !== '') {
    params.append('diagnosticId', filters.diagnosticId)
  }

  // status
  if (filters.status && filters.status !== '0') {
    params.append('status', filters.status)
  }

  // review (Sensibilidade) - só Raio-X; CSV
  if (filters.review && filters.review.length > 0) {
    params.append('review', filters.review.join(','))
  }

  // priority (Prioridade) - só Raio-X; CSV
  if (filters.priority && filters.priority.length > 0) {
    params.append('priority', filters.priority.join(','))
  }

  return params
}

/**
 * Desserializa URLSearchParams para DiagnosticFilters
 * - Converte strings de data para dayjs.Dayjs
 * - Converte string separada por vírgula para array
 * - Valores ausentes ficam como default (null, '', [])
 */
export const deserializeFilters = (
  params: URLSearchParams,
  initialFilters: DiagnosticFilters
): DiagnosticFilters => {
  const diagnosticTypesParam = params.get('diagnosticTypes')
  const subtypesParam = params.get('subtypes')
  const reviewParam = params.get('review')
  const priorityParam = params.get('priority')

  const filters: DiagnosticFilters = {
    subtypes: (subtypesParam
      ? (subtypesParam
          .split(',')
          .filter(item => item.trim() !== '') as ExamSubtype[])
      : initialFilters.subtypes) as ExamSubtype[],
    diagnosticTypes: (diagnosticTypesParam
      ? diagnosticTypesParam.split(',').filter(item => item.trim() !== '')
      : initialFilters.diagnosticTypes) as DiagnosticFilters['diagnosticTypes'],
    examDateStart: params.get('examDateStart')
      ? dayjs(params.get('examDateStart'))
      : null,
    examDateEnd: params.get('examDateEnd')
      ? dayjs(params.get('examDateEnd'))
      : null,
    patientId: params.get('patientId') || '',
    examId: params.get('examId') || '',
    diagnosticId: params.get('diagnosticId') || '',
    status: (params.get('status') as '0' | '1' | '2' | 'all') || '0',
    review: reviewParam
      ? reviewParam.split(',').filter(item => item.trim() !== '')
      : initialFilters.review,
    priority: priorityParam
      ? priorityParam.split(',').filter(item => item.trim() !== '')
      : initialFilters.priority
  }

  return filters
}

/**
 * Verifica se os filtros são diferentes dos filtros iniciais
 * Útil para saber se há algum filtro ativo
 */
export const hasActiveFilters = (
  filters: DiagnosticFilters,
  initialFilters: DiagnosticFilters
): boolean => {
  return (
    !isDefaultSubtypes(filters.subtypes) ||
    (filters.diagnosticTypes.length > 0 &&
      filters.diagnosticTypes[0] !== 'none') ||
    filters.examDateStart !== null ||
    filters.examDateEnd !== null ||
    filters.patientId.trim() !== '' ||
    filters.examId.trim() !== '' ||
    filters.diagnosticId.trim() !== '' ||
    filters.status !== initialFilters.status ||
    filters.review.length > 0 ||
    filters.priority.length > 0
  )
}
